/**
 * A kebab button and the menu it opens.
 *
 * **Why it exists.** This lived inside `ProviderBlock`, which was the only
 * thing that needed it. #2194 needs three more — one for the whole test
 * list, one for the open test, one per suite — and four copies of a menu
 * is four places for the dismiss behaviour to drift. So it moved here and
 * `ProviderBlock` uses it like everyone else; the extraction is meant to
 * be behaviour-neutral, and anything that looks odd here looked odd there.
 *
 * **What it owns.** Open state, dismissal, and how an item renders.
 * Nothing about what the items DO — the caller passes those, because that
 * is the only part that differs between four callers.
 *
 * **Dismissal is the fiddly part.** A menu that closes on outside click but
 * not on Escape traps a keyboard user; one that closes on either but does
 * not clean up its listeners leaks one per open. Both are handled once
 * here rather than four times.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { MoreHorizontal, MoreVertical } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface KebabMenuItem {
  /** React key, and the handle a test uses to find the item. */
  key: string
  label: string
  /** Optional second line under the label, in muted text. */
  detail?: string
  /** Optional short note at the right edge of the item, e.g. a status mark. */
  trailing?: ReactNode
  /** Optional leading icon, sized by the menu rather than the caller. */
  icon?: ReactNode
  onClick: () => void
  disabled?: boolean
  /** Renders in the destructive colour. Pair with `separator` so a
   * destructive item is never adjacent to a safe one it could be
   * mistaken for. */
  danger?: boolean
  /** Draws a divider ABOVE this item. */
  separator?: boolean
}

export interface KebabMenuProps {
  items: KebabMenuItem[]
  /** What the button announces to a screen reader. Say what it acts on —
   * "More actions" three times on one screen tells nobody which is which. */
  ariaLabel: string
  /** Vertical dots by default; horizontal where the row is tight. */
  orientation?: 'vertical' | 'horizontal'
  /** Extra classes for the trigger. */
  className?: string
  /** Narrower menus suit one-word actions; the default fits a sentence.
   * Wide fits items that carry a `detail` line. */
  width?: 'default' | 'narrow' | 'wide'
  /** Show a text button (e.g. "Assign to") instead of the dots. */
  triggerLabel?: string
  /** Disables the trigger, with `ariaLabel` or a caller tooltip saying why. */
  triggerDisabled?: boolean
  /** Optional heading line at the top of the open menu. */
  heading?: string
  /** Optional muted note at the bottom of the open menu. */
  footer?: string
  /** Render the open menu at the page level, pinned to the button, instead of
   * inside it. Needed wherever the button sits in a scrolling container (a
   * table cell): there the menu would otherwise be cut off at the container's
   * edge. It opens upward when there is no room below. Same approach as the
   * suites menu in `MultiSuiteChips`. */
  portal?: boolean
}

export function KebabMenu({
  items,
  ariaLabel,
  orientation = 'vertical',
  className,
  width = 'default',
  triggerLabel,
  triggerDisabled = false,
  heading,
  footer,
  portal = false,
}: KebabMenuProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  /** Where the page-level menu sits, measured from the button. */
  const [place, setPlace] = useState<{ top: number; right: number } | null>(null)

  // Measure after the menu renders, so its own height decides whether it fits
  // below the button or has to open above it. Kept inside the viewport.
  useLayoutEffect(() => {
    if (!portal || !open) { setPlace(null); return }
    const btn = triggerRef.current?.getBoundingClientRect()
    if (!btn) return
    const height = menuRef.current?.offsetHeight ?? 0
    const below = btn.bottom + 4
    const top = below + height > window.innerHeight - 8 && btn.top - 4 - height >= 8
      ? btn.top - 4 - height
      : below
    setPlace({ top, right: Math.max(8, window.innerWidth - btn.right) })
  }, [portal, open])

  // A page-level menu does not move with the page, so close it on scroll or
  // resize rather than leave it pointing at the wrong row.
  useEffect(() => {
    if (!portal || !open) return
    const close = () => setOpen(false)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [portal, open])

  // Close on a click outside the container or on Escape. Escape matters
  // for a keyboard user, who otherwise has no way out but to activate
  // something. Listeners are attached only while open and removed on
  // close, so repeated opening does not accumulate them.
  useEffect(() => {
    if (!open) return
    function handleOutside(e: MouseEvent) {
      const target = e.target as Node
      // The page-level menu is outside `ref`, so a click in it is not outside.
      if (menuRef.current?.contains(target)) return
      if (ref.current && !ref.current.contains(target)) setOpen(false)
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  if (items.length === 0) return null
  const Dots = orientation === 'vertical' ? MoreVertical : MoreHorizontal

  return (
    <div ref={ref} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="menu"
        disabled={triggerDisabled}
        onClick={() => setOpen(v => !v)}
        className={cn(
          triggerLabel
            ? 'h-7 px-2.5 inline-flex items-center gap-1 rounded border text-xs font-semibold whitespace-nowrap transition-colors border-border hover:bg-muted disabled:opacity-45 disabled:cursor-not-allowed'
            : 'w-7 h-7 flex items-center justify-center rounded border text-muted-foreground transition-colors',
          !triggerLabel && (open ? 'bg-background border-border' : 'border-transparent hover:bg-background hover:border-border'),
          className,
        )}
      >
        {triggerLabel ? <>{triggerLabel} <span aria-hidden>▾</span></> : <Dots className="h-4 w-4" />}
      </button>

      {open && (() => {
        const menu = (
        <div
          ref={menuRef}
          role="menu"
          style={portal ? { position: 'fixed', top: place?.top ?? -9999, right: place?.right ?? 0 } : undefined}
          className={cn(
            portal
              ? 'z-50 rounded-md border border-border bg-background shadow-md py-1'
              : 'absolute right-0 top-[calc(100%+4px)] z-10 rounded-md border border-border bg-background shadow-md py-1',
            width === 'narrow' ? 'min-w-[160px]' : width === 'wide' ? 'w-[320px]' : 'min-w-[220px]',
          )}
        >
          {heading && <div className="px-3 pt-1.5 pb-1 text-xs font-semibold text-muted-foreground">{heading}</div>}
          {items.map(item => (
            <div key={item.key}>
              {item.separator && <div className="my-1 h-px bg-border" role="separator" />}
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  // Close first: the handler may open a dialog, and a menu
                  // still on screen behind it reads as two things at once.
                  setOpen(false)
                  item.onClick()
                }}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  item.danger ? 'text-destructive hover:bg-destructive/10' : 'text-foreground hover:bg-muted',
                )}
              >
                {item.icon && (
                  <span className={cn(
                    'w-4 h-4 flex items-center justify-center flex-shrink-0',
                    item.danger ? 'text-destructive' : 'text-muted-foreground',
                  )}>
                    {item.icon}
                  </span>
                )}
                {item.detail || item.trailing ? (
                  <span className="flex flex-1 items-center justify-between gap-2 text-left">
                    <span className="flex flex-col">
                      <span>{item.label}</span>
                      {item.detail && <span className="text-xs text-muted-foreground">{item.detail}</span>}
                    </span>
                    {item.trailing && <span className="text-xs whitespace-nowrap">{item.trailing}</span>}
                  </span>
                ) : (
                  <span>{item.label}</span>
                )}
              </button>
            </div>
          ))}
          {footer && (
            <div className="mt-1 border-t border-border px-3 pt-2 pb-1 text-xs text-muted-foreground whitespace-normal">{footer}</div>
          )}
        </div>
        )
        // `createPortal` needs a real document; fall back to in place without one.
        return portal && typeof document !== 'undefined' ? createPortal(menu, document.body) : menu
      })()}
    </div>
  )
}
